import type { FC } from 'react';
import {
  IconAerialLift,
  IconAirConditioning,
  IconBarbell,
  IconBath,
  IconBeach,
  IconBeer,
  IconBook,
  IconCamera,
  IconChefHat,
  IconChessQueen,
  IconCoffee,
  IconConfetti,
  IconDeviceGamepad2,
  IconDeviceSpeaker,
  IconDeviceTv,
  IconDice6,
  IconFirstAidKit,
  IconGlassFull,
  IconGps,
  IconHeartHandshake,
  IconHotelService,
  IconJetpack,
  IconKayak,
  IconLamp,
  IconLifebuoy,
  IconMassage,
  IconMovie,
  IconPlug,
  IconPool,
  IconQuestionMark,
  IconRollercoaster,
  IconScubaMask,
  IconSofa,
  IconSunWind,
  IconSwimming,
  IconToolsKitchen2,
  IconWhirl,
  IconWifi,
  IconWindow,
  IconYoga,
} from '@tabler/icons-react';

export type TablerIcon = FC<{ size?: number | string }>;

const ICON_MAP: Record<string, TablerIcon> = {
  'aerial-lift': IconAerialLift,
  'air-conditioning': IconAirConditioning,
  barbell: IconBarbell,
  bath: IconBath,
  beach: IconBeach,
  beer: IconBeer,
  book: IconBook,
  camera: IconCamera,
  'chef-hat': IconChefHat,
  'chess-queen': IconChessQueen,
  coffee: IconCoffee,
  confetti: IconConfetti,
  'device-gamepad-2': IconDeviceGamepad2,
  'device-speaker': IconDeviceSpeaker,
  'device-tv': IconDeviceTv,
  'dice-6': IconDice6,
  'first-aid-kit': IconFirstAidKit,
  'glass-full': IconGlassFull,
  gps: IconGps,
  'heart-handshake': IconHeartHandshake,
  'hotel-service': IconHotelService,
  jetpack: IconJetpack,
  kayak: IconKayak,
  lamp: IconLamp,
  lifebuoy: IconLifebuoy,
  massage: IconMassage,
  movie: IconMovie,
  plug: IconPlug,
  pool: IconPool,
  rollercoaster: IconRollercoaster,
  'scuba-mask': IconScubaMask,
  sofa: IconSofa,
  'sun-wind': IconSunWind,
  swimming: IconSwimming,
  'tools-kitchen-2': IconToolsKitchen2,
  whirl: IconWhirl,
  wifi: IconWifi,
  window: IconWindow,
  yoga: IconYoga,
};

const FALLBACK_ICON: TablerIcon = IconQuestionMark;

const REACT_FORWARD_REF = Symbol.for('react.forward_ref');
const REACT_MEMO = Symbol.for('react.memo');

/**
 * Returns true only for values React can safely render as components.
 * Accepts functions and forwardRef/memo components; rejects elements,
 * primitives, and unrelated runtime exports.
 */
export const isIconComponent = (value: unknown): value is TablerIcon => {
  if (typeof value === 'function') {
    return true;
  }

  if (typeof value !== 'object' || value === null || !('$$typeof' in value)) {
    return false;
  }

  const typeTag = (value as { $$typeof: symbol }).$$typeof;
  return typeTag === REACT_FORWARD_REF || typeTag === REACT_MEMO;
};

/**
 * Resolve a facility icon key to a Tabler icon component.
 * Unknown, empty, or invalid mappings always fall back to IconQuestionMark.
 */
export const resolveFacilityIcon = (icon: string): TablerIcon => {
  if (!icon || typeof icon !== 'string') {
    return FALLBACK_ICON;
  }

  const mapped = ICON_MAP[icon];
  if (!isIconComponent(mapped)) {
    return FALLBACK_ICON;
  }

  return mapped;
};
